import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Navbar } from '../../components/Navbar';
import { BackButton } from '../../components/BackButton';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { User, Save, Truck } from 'lucide-react';
import { toast } from 'sonner';

export function DriverProfilePage() {
  const { user, updateProfile } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateProfile(formData);
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050003]" data-testid="driver-profile-page">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <BackButton to="/driver" />
          <h1 className="text-[#39FF14] font-mono text-2xl font-bold neon-text">
            {'>'} DRIVER_PROFILE
          </h1>
        </div>

        {/* Profile Card */}
        <div className="bg-[#0C0612] border border-[#2A0F3D] p-6">
          {/* User Info */}
          <div className="flex items-center gap-4 pb-6 border-b border-[#2A0F3D] mb-6">
            <div className="w-16 h-16 bg-[#2A0F3D] flex items-center justify-center">
              <Truck className="w-8 h-8 text-[#39FF14]" />
            </div>
            <div>
              <h2 className="text-[#39FF14] font-mono font-bold text-lg">{user?.name}</h2>
              <p className="text-[#4B6B52] font-mono text-sm">{user?.email}</p>
              <p className="text-[#00F0FF] font-mono text-xs uppercase mt-1">
                DRIVER ACCOUNT
              </p>
            </div>
          </div>

          {/* Edit Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">
                FULL_NAME
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
                placeholder="Your name"
                data-testid="driver-profile-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">
                PHONE_NUMBER
              </Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
                placeholder="555-0100"
                data-testid="driver-profile-phone"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] hover:shadow-[0_0_12px_rgba(57,255,20,0.6)] rounded-none h-12"
              data-testid="save-driver-profile"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="cursor-blink">█</span> SAVING...
                </span>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  SAVE CHANGES
                </>
              )}
            </Button>
          </form>

          {/* Account Info */}
          <div className="mt-6 pt-6 border-t border-[#2A0F3D]">
            <h3 className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider mb-4">ACCOUNT_INFO</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-[#4B6B52] font-mono text-sm">Email</span>
                <span className="text-[#A3C7A8] font-mono text-sm">{user?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#4B6B52] font-mono text-sm">Account Type</span>
                <span className="text-[#00F0FF] font-mono text-sm uppercase">DRIVER</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#4B6B52] font-mono text-sm">Member Since</span>
                <span className="text-[#A3C7A8] font-mono text-sm">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
